/**
 * Nodos de recurso (yacimientos) del mundo. Un nodo es una entidad estática que
 * se puede EXTRAER (minar / talar) para obtener un material y XP de la skill
 * asociada. Los yacimientos están SIEMPRE en el mismo lugar del mapa (posiciones
 * fijas/determinísticas): hay zonas dedicadas donde aparece cada mineral.
 *
 * Esta tanda implementa MINERÍA (skill_mining → menas). La leñería (árboles →
 * troncos) reusará este mismo subsistema en una tanda futura.
 *
 * Ver docs/MINING_AND_SMITHING.md.
 */

import type { SkillId } from '../types/skills';
import { TOOL_TIERS, TIER_MINING_LEVEL, type ToolTier } from './items';
import type { ToolType } from './tools';
import type { MapFishingSpot } from '../types/map';
import { FISH_SPECIES } from './fish';

/** Tamaño de un yacimiento → cuánta cantidad tiene antes de agotarse. */
export type ResourceNodeSize = 'small' | 'medium' | 'large';

/**
 * Entrada de la tabla de botín de un nodo: un item con su probabilidad (0..1) de salir por
 * intento exitoso. Ver `ResourceNodeType.produces`.
 */
export interface ProduceEntry {
  itemId: string;
  /** Probabilidad de que ESTE item salga (0..1). La suma de todas las entradas puede ser < 1;
   *  el remanente (`1 − Σchance`) es la chance de FALLO (no sale nada). */
  chance: number;
}

export interface ResourceNodeType {
  /** ID del tipo (ej. 'ore_node_bronze'). */
  id: string;
  /** Nombre visible del yacimiento (español). */
  name: string;
  /** Skill que gana XP al extraer de este nodo. */
  skill: SkillId;
  /** Herramienta requerida (ver TOOL_TYPES: 'pickaxe' minería, 'hatchet' tala, 'fishing_rod' pesca). */
  toolType: ToolType;
  /** Tier de material (para el gate de herramienta: pico de tier ≥ este − 1). */
  tier: ToolTier;
  /**
   * Gate por ITEM específico (opcional). Si está seteado, extraer de este nodo requiere
   * poseer ESA instancia exacta (no rota, inventario o equipada), en vez de la lógica de
   * tier de `findUsableTool`. Lo usa la PESCA: cada cardumen exige su caña puntual
   * (1 caña por pez), no una caña "de tier ≥". Ver docs/FISHING_SKILL.md.
   */
  requiredToolItemId?: string;
  /** Item que entrega cada extracción (pez/mena/madera primario; usado en avisos de gate y como
   *  fallback cuando NO hay `produces`). */
  producesItemId: string;
  /**
   * Tabla de botín OPCIONAL: varios items, cada uno con su probabilidad de salir por intento
   * (0..1). Si la suma es < 1, el remanente es la chance de FALLO (no sale nada, pero el intento
   * se consume). Si está definida, REEMPLAZA a `producesItemId` al extraer (el servidor tira el
   * dado con `rollNodeProduce`). Si falta, el nodo entrega siempre `producesItemId` (comportamiento
   * clásico de minería/tala/pesca simple). Lo usa la PESCA para cardúmenes con varias especies.
   */
  produces?: ProduceEntry[];
  /** Nivel de la skill requerido para extraer. */
  requiredLevel: number;
  /** XP otorgada por unidad extraída. */
  xpPerGather: number;
  /** Tiempo de respawn (ms) desde que se agota hasta que se rellena. */
  respawnMs: number;
  /** Cantidad de variantes de sprite disponibles (1..5). */
  variantCount: number;
  /** Cantidad total por tamaño (fallback/compat; la cantidad real sale del rango). */
  sizes: Record<ResourceNodeSize, number>;
  /** Rango de cantidad total del nodo: al spawnear/respawnear se rolea un valor entre
   *  `qtyMin` y `qtyMax` (inclusive). Los minerales/árboles fáciles rinden más. */
  qtyMin: number;
  qtyMax: number;
  /**
   * Probabilidad (0..1) de que un intento de extracción tenga ÉXITO. Si falta, se asume 1
   * (siempre sale, comportamiento clásico de tala/pesca-simple). Lo usa la MINERÍA: cada
   * yacimiento tiene su % propio en escalera descendente por tier (hierro alto → obsidiana
   * bajo; carbón fijo). Al fallar, el intento se consume pero NO da material/XP ni gasta el
   * nodo. El mod `miningSuccessChanceBonus` del player lo MULTIPLICA (nodo del Enano = ×2).
   * Ver docs/MINING_AND_SMITHING.md §Flujo de extracción.
   */
  successChance?: number;
}

/** XP de minería por tier (inspirado en OSRS). */
const TIER_MINING_XP: Record<ToolTier, number> = {
  iron: 35,
  steel: 50,
  mithril: 80,
  adamant: 95,
  rune: 125,
  obsidian: 150,
};

/** Respawn (ms) por tier (a la MITAD del original): 5s (hierro) → 120s (obsidiana). */
const TIER_RESPAWN_MS: Record<ToolTier, number> = {
  iron: 5000,
  steel: 10000,
  mithril: 22500,
  adamant: 45000,
  rune: 90000,
  obsidian: 120000,
};

/**
 * % de ÉXITO al minar por tier (escalera descendente): los minerales fáciles casi nunca
 * fallan, los mejores fallan seguido → obsidiana es la más grindera (pináculo). El carbón
 * va aparte (fijo, ver abajo). Tunable. Ver docs/MINING_AND_SMITHING.md.
 */
const TIER_MINING_SUCCESS: Record<ToolTier, number> = {
  iron: 0.9,
  steel: 0.79,
  mithril: 0.68,
  adamant: 0.57,
  rune: 0.46,
  obsidian: 0.35,
};

/** Nombre visible del mineral por tier (para el nombre del yacimiento). */
const TIER_ORE_LABEL: Record<ToolTier, string> = {
  iron: 'Hierro',
  steel: 'Acero',
  mithril: 'Mithril',
  adamant: 'Adamantita',
  rune: 'Runita',
  obsidian: 'Obsidiana',
};

/** Cantidad por tamaño (fallback común; los yacimientos usan su capacidad por tier). */
export const NODE_SIZE_QUANTITY: Record<ResourceNodeSize, number> = {
  small: 5,
  medium: 12,
  large: 25,
};

/**
 * Rango de cantidad [min, max] por tier, INVERTIDO: los minerales fáciles rinden MÁS que los
 * mejores (Hierro 8~12 → Obsidiana 1~2). Al spawnear/respawnear se rolea un valor entre min y
 * max. El Carbón (abundante) va aparte, más alto.
 */
const TIER_NODE_QTY: Record<ToolTier, [number, number]> = {
  iron: [8, 12],
  steel: [6, 9],
  mithril: [4, 7],
  adamant: [3, 5],
  rune: [2, 3],
  obsidian: [1, 2],
};

/** Deriva los 3 tamaños desde una capacidad 'medium' (compat con `sizes`). */
function sizesFromCapacity(medium: number): Record<ResourceNodeSize, number> {
  return { small: Math.round(medium * 0.6), medium, large: Math.round(medium * 1.8) };
}

/** Catálogo de tipos de yacimiento, uno por tier minable (iron→obsidian, sin dragón). */
export const RESOURCE_NODE_TYPES: Record<string, ResourceNodeType> = (function build(): Record<
  string,
  ResourceNodeType
> {
  const out: Record<string, ResourceNodeType> = {};
  for (const tier of TOOL_TIERS) {
    const id = `ore_node_${tier}`;
    out[id] = {
      id,
      name: `Yacimiento de ${TIER_ORE_LABEL[tier]}`,
      skill: 'skill_mining',
      toolType: 'pickaxe',
      tier,
      producesItemId: `material_ore_${tier}`,
      requiredLevel: TIER_MINING_LEVEL[tier],
      xpPerGather: TIER_MINING_XP[tier],
      respawnMs: TIER_RESPAWN_MS[tier],
      // 4 variantes visuales = los 4 MODELOS de la hoja art_src/mining.BMP (2 peñascos grandes 2×2
      // + 2 rocas chicas 1×1), recoloreados por tier. Sin agotamiento visual (full/mid/low iguales;
      // sólo depleted en gris). El nodo elige variante por seed al spawnear y la re-rollea al
      // respawnear (ResourceNodeManager). Sprites: ore_node_<tier>[2|3|4]_<stage> (batch_deposits).
      variantCount: 4,
      qtyMin: TIER_NODE_QTY[tier][0],
      qtyMax: TIER_NODE_QTY[tier][1],
      sizes: sizesFromCapacity(TIER_NODE_QTY[tier][1]),
      successChance: TIER_MINING_SUCCESS[tier],
    };
  }

  // ── CARBÓN (standalone) ──
  // No es un tier: no tiene pico/lingote/colección propios (estilo RuneScape). Es un
  // combustible que se mina con pico y se funde junto a la mena para forjar del Acero
  // en adelante (cantidad = TIER_COAL_COST[tier], ver items.ts). Se mina al nivel del
  // Acero (18); `tier:'steel'` sólo alimenta el gate de pico (≥ steel − 1 = hierro).
  // Sprite ore_node_coal_<stage> (una silueta, gen_tinted_assets.py con base deposit2).
  out['ore_node_coal'] = {
    id: 'ore_node_coal',
    name: 'Veta de Carbón',
    skill: 'skill_mining',
    toolType: 'pickaxe',
    tier: 'steel',
    producesItemId: 'material_coal',
    requiredLevel: TIER_MINING_LEVEL['steel'],
    xpPerGather: 50,
    respawnMs: 10000, // a la mitad
    variantCount: 1,
    // Combustible abundante (estilo RuneScape): rinde más por veta que un mineral.
    qtyMin: 10,
    qtyMax: 15,
    sizes: sizesFromCapacity(15),
    // % de éxito FIJO (no sigue la escalera de tiers): el carbón falla bastante a propósito.
    successChance: 0.25,
  };
  return out;
})();

/**
 * ── TALA / LEÑERÍA (skill_woodcutting) ──
 * Cada ESPECIE de árbol es un tipo de nodo propio (toolType 'hatchet'): da su madera
 * única, con distinto nivel de skill, capacidad y respawn (curva estilo OSRS: cuanto
 * más raro, más nivel/XP/respawn y más madera por árbol). Las variantes visuales 'a'
 * (mismo árbol, otra silueta) van por `variantCount` (2 → hay tree_node_<sp>2_*).
 * El `tier` sólo se usa para el gate de HACHA (hacha de tier ≥ tier − 1), en paralelo
 * a la minería. Sprites en assets/world/nodes/tree_node_<species>[2]_{tree,stump}.png.
 */
interface TreeSpeciesDef {
  species: string; // maple, willow, oak, birch, palm, jacaranda, pine
  wood: string; // item que entrega (material_wood_<species>)
  label: string; // nombre visible del árbol
  wcLevel: number; // nivel de Leñería requerido
  tier: ToolTier; // gate de hacha
  xp: number; // XP por unidad
  respawnMs: number; // respawn del tocón
  capacity: number; // madera por árbol (tamaño 'medium'; small/large derivan)
  variants: number; // 1 o 2 siluetas
}

// Capacidad INVERTIDA + reducida 50% (pedido): los árboles fáciles rinden más madera que los
// difíciles (arce 17 → pino 5). Antes era al revés (arce 10 → pino 34).
export const TREE_SPECIES: TreeSpeciesDef[] = [
  { species: 'maple', wood: 'material_wood_maple', label: 'Arce', wcLevel: 1, tier: 'iron', xp: 25, respawnMs: 6000, capacity: 17, variants: 1 },
  { species: 'willow', wood: 'material_wood_willow', label: 'Sauce', wcLevel: 15, tier: 'iron', xp: 45, respawnMs: 12000, capacity: 15, variants: 1 },
  { species: 'oak', wood: 'material_wood_oak', label: 'Roble', wcLevel: 30, tier: 'iron', xp: 65, respawnMs: 22000, capacity: 13, variants: 1 },
  { species: 'birch', wood: 'material_wood_birch', label: 'Abedul', wcLevel: 45, tier: 'steel', xp: 85, respawnMs: 45000, capacity: 11, variants: 2 },
  { species: 'palm', wood: 'material_wood_palm', label: 'Palmera', wcLevel: 60, tier: 'mithril', xp: 110, respawnMs: 90000, capacity: 9, variants: 2 },
  { species: 'jacaranda', wood: 'material_wood_jacaranda', label: 'Jacarandá', wcLevel: 75, tier: 'adamant', xp: 135, respawnMs: 150000, capacity: 7, variants: 2 },
  { species: 'pine', wood: 'material_wood_pine', label: 'Pino', wcLevel: 90, tier: 'rune', xp: 160, respawnMs: 180000, capacity: 5, variants: 2 },
];

for (const t of TREE_SPECIES) {
  RESOURCE_NODE_TYPES[`tree_node_${t.species}`] = {
    id: `tree_node_${t.species}`,
    name: `${t.label}`,
    skill: 'skill_woodcutting',
    toolType: 'hatchet',
    tier: t.tier,
    producesItemId: t.wood,
    requiredLevel: t.wcLevel,
    xpPerGather: t.xp,
    respawnMs: Math.round(t.respawnMs / 2), // respawns a la mitad
    variantCount: t.variants,
    qtyMin: t.capacity,
    qtyMax: t.capacity,
    sizes: {
      small: Math.round(t.capacity * 0.6),
      medium: t.capacity,
      large: Math.round(t.capacity * 1.8),
    },
  };
}

/**
 * ── PESCA (skill_fishing) ──
 * Cada ESPECIE de pez (ver `shared/data/fish.ts`) es un tipo de nodo propio (un CARDUMEN,
 * `toolType: 'fishing_rod'`). A diferencia de minería/tala, el gate NO es por tier de
 * herramienta sino por la CAÑA puntual del pez (`requiredToolItemId` → tool_fishing_rod_<sp>),
 * crafteada en Carpintería con la madera 1:1 de la especie de árbol del mismo escalón.
 * Ver docs/FISHING_SKILL.md.
 */
for (const f of FISH_SPECIES) {
  RESOURCE_NODE_TYPES[`fish_node_${f.species}`] = {
    id: `fish_node_${f.species}`,
    name: `Cardumen de ${f.label}`,
    skill: 'skill_fishing',
    toolType: 'fishing_rod',
    tier: f.tier,
    requiredToolItemId: `tool_fishing_rod_${f.species}`,
    producesItemId: `material_fish_${f.species}`,
    requiredLevel: f.fishLevel,
    xpPerGather: f.xp,
    respawnMs: f.respawnMs,
    variantCount: 1,
    qtyMin: f.capacity,
    qtyMax: f.capacity,
    sizes: {
      small: Math.round(f.capacity * 0.6),
      medium: f.capacity,
      large: Math.round(f.capacity * 1.8),
    },
  };
}

// ── Cardumen MIXTO de referencia ──
// El cardumen de sardina (nivel 1, testeable ni bien entrás) suelta VARIAS especies con su
// probabilidad, como demo del sistema de tabla de botín (`produces`): sardina 70% / trucha 25%,
// dejando 5% de FALLO (el intento se consume, no el cardumen). El gate sigue siendo la caña de
// sardina y `producesItemId` sigue siendo la sardina (pez primario para los avisos). Sirve de
// referencia viva para no olvidar la lógica al agregar más cardúmenes mixtos.
if (RESOURCE_NODE_TYPES['fish_node_sardine']) {
  RESOURCE_NODE_TYPES['fish_node_sardine'].produces = [
    { itemId: 'material_fish_sardine', chance: 0.7 },
    { itemId: 'material_fish_trout', chance: 0.25 },
  ];
}

export function getResourceNodeType(typeId: string): ResourceNodeType | undefined {
  return RESOURCE_NODE_TYPES[typeId];
}

/**
 * Entradas de botín de un nodo, normalizadas para UI (tooltip + íconos del piso). Si el nodo
 * tiene `produces`, las devuelve tal cual; si no, sintetiza una entrada única con el
 * `producesItemId` al 100%. Así el cliente pinta siempre "N filas de item con su %".
 */
export function getNodeProduceEntries(type: ResourceNodeType): ProduceEntry[] {
  if (type.produces && type.produces.length > 0) return type.produces;
  return [{ itemId: type.producesItemId, chance: 1 }];
}

/**
 * Tira el botín de un nodo en un intento exitoso. Sin `produces` → devuelve siempre
 * `producesItemId` (nunca falla). Con `produces` → tira ponderado y devuelve el itemId elegido,
 * o `null` si cayó en el remanente de FALLO (`1 − Σchance`). Server-side (usa `Math.random`).
 */
export function rollNodeProduce(type: ResourceNodeType): string | null {
  if (!type.produces || type.produces.length === 0) return type.producesItemId;
  const roll = Math.random();
  let acc = 0;
  for (const entry of type.produces) {
    acc += entry.chance;
    if (roll < acc) return entry.itemId;
  }
  return null; // cayó en el remanente = fallo (no sale nada)
}

/** Índice de un tier en la escala (para el gate de herramienta). -1 si no existe. */
export function tierIndex(tier: string): number {
  return (TOOL_TIERS as readonly string[]).indexOf(tier);
}

/**
 * Colocación fija de un yacimiento en el mapa. Determinística: los minerales
 * están SIEMPRE en el mismo lugar. `variant` opcional (si falta, se deriva del
 * índice al spawnear).
 */
export interface ResourceNodePlacement {
  typeId: string;
  x: number;
  y: number;
  size: ResourceNodeSize;
  variant?: number;
  /**
   * Área móvil (cardúmenes de pesca). Si `areaW`/`areaH` > 0, `x`/`y` son la esquina
   * SUPERIOR-IZQUIERDA del rectángulo y el nodo elige un slot random del área al spawnear/
   * respawnear (anti-macro). Sin ellos, el nodo es fijo en `x`/`y`. Ver docs/FISHING_SKILL.md.
   */
  areaW?: number;
  areaH?: number;
}

// Colección de MINERÍA: 7 tiers (uno por columna, bronce→obsidiana) cada 3 tiles (misma
// separación horizontal que la leñería) desde x=621. Cada tier muestra sus 4 VARIANTES
// visuales apiladas en COLUMNA (y637 var1 abajo, y634 var2, y631 var3, y628 var4) para ver que
// dos yacimientos del mismo mineral no son iguales. Debajo del nodo de y637 (y639/640/641) van
// menas/lingotes/picos alineados por la vitrina de debug
// (server/src/rooms/systems/DebugShowcaseSpawner.ts §spawnSkillCollections).
// Estas X son la fuente de verdad: si cambian, actualizar los anchorX de esos grids.
export const MAP1_MINING_NODES: ResourceNodePlacement[] = [
  ...TOOL_TIERS.flatMap((tier, i) =>
    [1, 2, 3, 4].map((variant) => ({
      typeId: `ore_node_${tier}`,
      x: 621 + i * 3,
      y: 637 - (variant - 1) * 3, // var1 en y637 (alineada con menas/lingotes/picos), var2/3/4 arriba
      size: 'medium' as ResourceNodeSize,
      variant,
    }))
  ),
  // Carbón: veta standalone (1 variante) a la derecha del último tier (obsidiana en
  // x636 → carbón en x639), alineada en y637 con la fila de menas.
  { typeId: 'ore_node_coal', x: 621 + TOOL_TIERS.length * 3, y: 637, size: 'medium', variant: 1 },
];

// Colección de LEÑERÍA: 7 árboles (uno por especie) cada 3 tiles desde (621,650),
// 2 tiles más abajo de la minería (separación pedida). Las maderas/hachas se alinean
// debajo (y652/654) vía spawnSkillCollections. Estas X son la fuente de verdad.
export const MAP1_WOODCUTTING_NODES: ResourceNodePlacement[] = TREE_SPECIES.map((t, i) => ({
  typeId: `tree_node_${t.species}`,
  x: 621 + i * 3,
  y: 650,
  size: 'medium',
  variant: 1,
}));

/**
 * Placements de yacimientos (minería + leñería) de un mapa. Fuente de verdad de
 * las posiciones fijas — el server los spawnea y el World Map los dibuja como
 * capa "Recursos". Hoy sólo map1 tiene yacimientos. Al agregar zonas nuevas de
 * minería/leñería, sumarlas acá y aparecen solas en el mapa. Ver docs/WORLD_MAP.md.
 */
// Colección de PESCA (debug): 7 cardúmenes (uno por especie), cada uno con un ÁREA móvil 3×3
// (anti-macro: el cardumen aparece en un slot random del área y salta a otro al respawnear).
// Espaciados cada 4 tiles desde x=620 para que las áreas 3×3 no se pisen. y=658. Los
// cardúmenes editor-defined (Fase B, panel del Map Editor → fishingSpots[]) los reemplazarán.
export const MAP1_FISHING_NODES: ResourceNodePlacement[] = FISH_SPECIES.map((f, i) => ({
  typeId: `fish_node_${f.species}`,
  x: 620 + i * 4,
  y: 658,
  size: 'medium',
  variant: 1,
  areaW: 3,
  areaH: 3,
}));

export function getResourceNodePlacementsForMap(mapId: string): ResourceNodePlacement[] {
  if (mapId === 'map1') return [...MAP1_MINING_NODES, ...MAP1_WOODCUTTING_NODES, ...MAP1_FISHING_NODES];
  return [];
}

/**
 * Convierte los cardúmenes definidos en el mapa (`MapData.fishingSpots`, editor-defined) en
 * placements de nodo con ÁREA móvil. El rect del cardumen es el área; el slot inicial lo elige
 * el ResourceNodeManager al spawnear. Ver docs/FISHING_SKILL.md.
 */
export function fishingSpotsToPlacements(spots: MapFishingSpot[]): ResourceNodePlacement[] {
  return spots.map((s) => ({
    typeId: s.typeId,
    x: s.rect.x,
    y: s.rect.y,
    size: 'medium' as ResourceNodeSize,
    variant: 1,
    areaW: Math.max(1, s.rect.w),
    areaH: Math.max(1, s.rect.h),
  }));
}
